import React from "react"
import PropTypes from "prop-types"
import cn from "classnames"
import { propsUtil } from "@asphalt-react/helper"

import { resolveDisplaySizeProp, resolveIntent } from "./props.js"
import styles from "./display.module.css"
import intentStyles from "./intent.module.css"

const { concatClasses, resolvePropCollision } = propsUtil

export const Display = ({
  children,
  size = "m",
  p = false,
  span = false,
  div = false,
  primary = false,
  secondary = false,
  brand = false,
  onBrand = false,
  muted = false,
  ...props
}) => {
  /* eslint-disable-next-line react/prop-types */
  const { style, className, ...rest } = props

  const { collision, value: Tag } = resolvePropCollision(
    {
      p,
      span,
      div,
    },
    "p"
  )

  if (collision) {
    console.warn(`Display: Prop collision detected, falling back to "${Tag}".`)
  }

  const { collision: intentCollision, value: intent } = resolveIntent({
    primary,
    secondary,
    brand,
    onBrand,
    muted,
  })

  if (intentCollision) {
    console.warn(
      `Display: Multiple intents detected, falling back to "${intent}" intent.`
    )
  }

  const classes = cn(
    styles.Display,
    intentStyles[intent],
    styles[`${concatClasses("display", resolveDisplaySizeProp(size))}`]
  )

  return (
    <Tag {...rest} className={classes}>
      {children}
    </Tag>
  )
}

Display.displayName = "Display"

Display.propTypes = {
  /**
   * React node for display content.
   */
  children: PropTypes.node.isRequired,
  /**
   * Controls the size of display.
   */
  size: PropTypes.oneOf(["s", "m", "l", "xl", "xxl"]),
  /**
   * render the display content in a `p` tag.
   */
  p: PropTypes.bool,
  /**
   * render the display content in a `span` tag.
   */
  span: PropTypes.bool,
  /**
   * render the display content in a `div` tag.
   */
  div: PropTypes.bool,
  /**
   * Renders the text with primary intent. This is the default intent.
   */
  primary: PropTypes.bool,
  /**
   * Renders the text with secondary intent.
   */
  secondary: PropTypes.bool,
  /**
   * Renders the text with brand intent.
   */
  brand: PropTypes.bool,
  /**
   * Renders the text with appropriate contrast ratio to backgrounds with brand color.
   */
  onBrand: PropTypes.bool,
  /**
   * Renders the text with muted intent.
   */
  muted: PropTypes.bool,
}

Display.defaultProps = {
  size: "m",
  p: false,
  span: false,
  div: false,
  primary: false,
  secondary: false,
  brand: false,
  onBrand: false,
  muted: false,
}
